import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatMoney } from 'common'
import ExpenseStatusLabel from 'employeeDashboard/common/ExpenseStatusLabel'

import {
  NamedItem,
  OrderedPageFilter,
  PagedList,
  PerksReimbursementRequestsResponse,
  deleteExpenseApi,
  getPerksReimbursementRequests,
} from 'core'
import { defaultFilter } from 'core/application/commons/orderedPageFilter'
import { ExpenseStatus } from 'core/domain/expenses/expenseStatus'

import ViewAttachmentsModal from 'common/components/expenseAttachments/ViewAttachmentsModal'

import { Card, Modal, Notification, Table } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'

import { useQueryRequestV2 } from '../../../../common/hooks'
import useCommandRequest from '../../../../common/hooks/useCommandRequest'
import MoreActionsPerks from './MoreActionsPerks'

type PerksEmployeeReimbursementRequestsProps = {
  planPeriodId: string
}

const PerksEmployeeReimbursementRequests: React.FC<PerksEmployeeReimbursementRequestsProps> = (props) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])
  const [sendDeleteRequest, isDeleteRequestLoading] = useCommandRequest()

  const [viewFilesVisible, setViewFilesVisible] = useState<boolean>(false)
  const [viewFilesForExpenseId, setViewFilesForExpenseId] = useState<string>('')

  const {
    data: pagedReimbursementRequests,
    reload: reloadPagedReimbursementRequests,
    sendQuery: sendGetExpensesQuery,
    isLoading: isPagedReimbursementRequestsLoading,
  } = useQueryRequestV2<PagedList<PerksReimbursementRequestsResponse>>({
    queryFn: () => {
      return getPerksReimbursementRequests(props.planPeriodId, filter)
    },
    enable: !!props.planPeriodId,
  })
  const [filter, setFilter] = useState<OrderedPageFilter>({
    ...defaultFilter,
    orderColumn: 'expenseNumber',
  })

  useEffect(() => {
    ;(async () => {
      await sendGetExpensesQuery(() => getPerksReimbursementRequests(props.planPeriodId, filter))
    })()
  }, [filter])

  const viewAttachments = async (expenseId) => {
    setViewFilesVisible(true)
    setViewFilesForExpenseId(expenseId)
  }

  useEffect(() => {
    reloadPagedReimbursementRequests()
  }, [props.planPeriodId])

  const onDeleteExpense = (expenseId: string): void => {
    Modal.confirm({
      title: translate('reimbursement-requests.confirm-remove-expense-title'),
      content: translate('reimbursement-requests.confirm-remove-expense-content'),
      okText: translate('translation:general.delete'),
      cancelText: translate('reimbursement-requests.confirm-keep-button'),
      onOk: async () => {
        const result = await sendDeleteRequest(() => deleteExpenseApi(expenseId))
        if (result.completed) {
          Notification.open({
            type: 'success',
            message: translate('reimbursement-requests.expense-deleted-successfully-message'),
          })
          await sendGetExpensesQuery(() => getPerksReimbursementRequests(props.planPeriodId, filter))
        }
      },
    })
  }
  const columns: any[] = [
    {
      title: translate('reimbursement-requests.expense-id-label'),
      dataIndex: 'expenseNumber',
      key: 'expenseNumber',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: NamedItem) => category.name,
    },
    {
      title: translate('reimbursement-requests.service-provider-label'),
      dataIndex: 'serviceProvider',
      key: 'serviceProvider',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Submitted on',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (item: Date) => parseZoneFormat(item),
    },
    {
      title: 'Status',
      dataIndex: 'expenseStatus',
      key: 'expenseStatus',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (item: ExpenseStatus) => <ExpenseStatusLabel expenseStatus={item} key={item} />,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (item: number) => formatMoney(item, true),
    },
    {
      title: 'Actions',
      dataIndex: 'Actions',
      key: 'Actions',
      className: 'center',
      render: (item: number, record: any) => {
        return (
          <>
            {record.expenseStatus === ExpenseStatus.Verified && !record.requireUploadAttachment ? (
              <div className=" center ">
                <h6>&nbsp;</h6>
              </div>
            ) : (
              <div className=" center ">
                <MoreActionsPerks
                  expense={record}
                  onDelete={onDeleteExpense}
                  onViewFiles={() => viewAttachments(record.id)}
                  viewAttachments={record.requireUploadAttachment}
                  planPeriodId={props.planPeriodId}
                />
              </div>
            )}
          </>
        )
      },
    },
  ]

  const columnToSort: any = columns.find((c) => c.key === filter.orderColumn)
  if (columnToSort) {
    columnToSort.defaultSortOrder = filter.orderDescending ? 'descend' : 'ascend'
  }

  const onTableFilterChanged = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      ...filter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'expenseNumber',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  return (
    <>
      <Card title={<h5 className="mb-0">{translate('reimbursement-requests.title')}</h5>} className="mb-16">
        <Table
          dataSource={pagedReimbursementRequests?.items}
          onChange={onTableFilterChanged}
          loading={isPagedReimbursementRequestsLoading}
          columns={columns}
          scroll={{ x: 1000 }}
          pagination={{
            total: pagedReimbursementRequests?.totalItemCount,
            pageSize: pagedReimbursementRequests?.pageSize,
            current: pagedReimbursementRequests?.pageNumber,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '40'],
          }}
        />
      </Card>
      {viewFilesForExpenseId && (
        <ViewAttachmentsModal
          visible={viewFilesVisible}
          onClose={() => setViewFilesVisible(false)}
          expenseId={viewFilesForExpenseId}
        />
      )}
    </>
  )
}

export default PerksEmployeeReimbursementRequests
